import { css } from '@emotion/react'
import { Modal, ModalBody, ModalSidebar, Text } from 'bold-ui'
import { confirm } from 'components/modals/confirm'
import { MChatPerguntaEnum, MChatRiscoEnum } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { RefObject, useRef } from 'react'
import { metaPath } from 'util/metaPath'
import { LogEventType } from 'view/atendimentos/atendimento-individual/logFinalizarAtendimento'

import { ConsultaSeguimentoPerguntasDiffModalBody } from '../consulta-seguimento-aplicacao/ConsultaSeguimentoPerguntasDiffModalBody'
import { ConsultaSeguimentoModalTab } from '../enums-consultaSeguimento'
import { useConsultaSeguimentoAplicacao } from '../hooks/useConsultaSeguimentoAplicacao'
import { useConsultaSeguimentoModal } from '../hooks/useConsultaSeguimentoModal'
import { ConsultaSeguimentoFieldModel, MChatPerguntaPrincipal } from '../model-consultaSeguimento'
import { ConsultaSeguimentoModalFooter } from './ConsultaSeguimentoModalFooter'
import { ConsultaSeguimentoModalStepper } from './ConsultaSeguimentoModalStepper'
import { ConsultaSeguimentoModalTabAdicionar } from './ConsultaSeguimentoModalTabAdicionar'
import { ConsultaSeguimentoModalTabResultado } from './ConsultaSeguimentoModalTabResultado'
import { ConsultaSeguimentoModalTabSelecao } from './ConsultaSeguimentoModalTabSelecao'

interface ConsultaSeguimentoModalProps {
  valueInCache: ConsultaSeguimentoFieldModel | undefined
  isConsultaSeguimentoDesatualizada: boolean
  mChatRisco: MChatRiscoEnum | undefined
  mChatPerguntasFalhas: ReadonlyArray<MChatPerguntaPrincipal>
  aplicarButtonRef: RefObject<HTMLButtonElement>
  onSave: (values: ConsultaSeguimentoFieldModel) => void
}

const path = metaPath<ConsultaSeguimentoFieldModel>()

export function ConsultaSeguimentoModal(props: Readonly<ConsultaSeguimentoModalProps>) {
  const {
    valueInCache,
    isConsultaSeguimentoDesatualizada = false,
    mChatRisco,
    mChatPerguntasFalhas,
    aplicarButtonRef,
    onSave,
  } = props

  const goNextButtonRef = useRef<HTMLButtonElement>(null)
  const backToSelecaoButtonRef = useRef<HTMLButtonElement>(null)
  const backToAplicacaoButtonRef = useRef<HTMLButtonElement>(null)
  const backQuestionButtonRef = useRef<HTMLButtonElement>(null)
  const modalBodyRef = useRef<HTMLDivElement>(null)

  const numberOfPerguntasFalhasNoMChat = mChatPerguntasFalhas?.length
  const hasPerguntasFalhasNoMChat = numberOfPerguntasFalhasNoMChat > 0
  const isMChatRiscoBaixo = hasPerguntasFalhasNoMChat && numberOfPerguntasFalhasNoMChat < 3

  const { analytics } = useAnalytics()

  const confirmCloseModalWithAnalytics = (onConfirm: () => void) => {
    confirmCloseModal(onConfirm, analytics.logEvent)
  }

  const {
    currentTab,
    handleCloseModal,
    handleGoToAplicacao,
    handleGoBackToAplicacao,
    handleGoToResultado,
    handleGoToSelecao,
  } = useConsultaSeguimentoModal({
    valueInCache,
    isConsultaSeguimentoDesatualizada,
    aplicarButtonRef,
    onConfirmCloseModal: confirmCloseModalWithAnalytics,
  })

  const {
    selectionState,
    clearStartFailed,
    start,
    goToNextQuestion,
    goToPreviousQuestion,
  } = useConsultaSeguimentoAplicacao({
    modalBodyRef,
    backQuestionButtonRef,
    backToAplicacaoButtonRef,
    backToSelecaoButtonRef,
    valueInCache,
    mChatPerguntasFalhas,
    onConfirmSelectionChanges: confirmSelectionChangesModal,
    onStart: handleGoToAplicacao,
    onFinish: handleGoToResultado,
    onSave,
  })

  return (
    <Modal open closeOnBackdropClick={false} onClose={handleCloseModal} style={styles.modal}>
      <ModalSidebar position='left' style={styles.sidebar}>
        <ConsultaSeguimentoModalStepper name={path.aplicacao} currentTab={currentTab} />
      </ModalSidebar>

      <ModalBody ref={modalBodyRef}>
        {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
          <ConsultaSeguimentoModalTabSelecao
            path={path}
            mChatRisco={mChatRisco}
            mChatPerguntasFalhas={mChatPerguntasFalhas}
            selectionState={selectionState}
            clearStartFailed={clearStartFailed}
          />
        )}
        {currentTab === ConsultaSeguimentoModalTab.APLICACAO && (
          <ConsultaSeguimentoModalTabAdicionar
            path={path}
            containerRef={modalBodyRef}
            goNextButtonRef={goNextButtonRef}
            backToSelecaoButtonRef={backToSelecaoButtonRef}
            backQuestionButtonRef={backQuestionButtonRef}
            onGoBackToSelecao={handleGoToSelecao}
            onGoToPreviousQuestion={goToPreviousQuestion}
          />
        )}
        {currentTab === ConsultaSeguimentoModalTab.RESULTADO && (
          <ConsultaSeguimentoModalTabResultado
            value={valueInCache}
            backToAplicacaoButtonRef={backToAplicacaoButtonRef}
            onGoBackToAplicacao={handleGoBackToAplicacao}
          />
        )}
      </ModalBody>

      <ConsultaSeguimentoModalFooter
        name={path}
        goNextButtonRef={goNextButtonRef}
        currentTab={currentTab}
        isMChatRiscoBaixo={isMChatRiscoBaixo}
        startFailed={selectionState.failed}
        onStart={start}
        onGoToNextQuestion={goToNextQuestion}
        onCloseModal={handleCloseModal}
      />
    </Modal>
  )
}

const confirmCloseModal = (onConfirm: () => void, analyticsLogEvent: LogEventType) => {
  confirm({
    title: 'Deseja sair sem finalizar a Entrevista de Seguimento?',
    icon: {
      name: 'exclamationTriangleOutline',
      fill: 'alert',
    },
    confirmLabel: 'Sair da tela',
    cancelLabel: 'Continuar aqui',
    type: 'primary',
    size: 'small',
    onConfirm: () => {
      analyticsLogEvent('click_cancelar_mchatrf')
      onConfirm()
    },
    render: () => <Text>As alterações realizadas serão perdidas, inclusive resultados já calculados.</Text>,
  })()
}

const confirmSelectionChangesModal = async (
  perguntasAnteriores: ReadonlyArray<MChatPerguntaEnum>,
  perguntasAtuais: ReadonlyArray<MChatPerguntaEnum>,
  perguntasObrigatorias: ReadonlyArray<MChatPerguntaEnum>,
  perguntasComRespostas: ReadonlyArray<MChatPerguntaEnum>
) => {
  return confirm({
    title: 'Deseja continuar a entrevista com estas alterações?',
    icon: {
      name: 'infoCircleOutline',
      fill: 'primary',
    },
    confirmLabel: 'Continuar entrevista',
    cancelLabel: 'Voltar',
    type: 'primary',
    size: 'auto',
    depthLevel: 2,
    render: () => (
      <ConsultaSeguimentoPerguntasDiffModalBody
        perguntasAnteriores={perguntasAnteriores}
        perguntasAtuais={perguntasAtuais}
        perguntasObrigatorias={perguntasObrigatorias}
        perguntasComRespostas={perguntasComRespostas}
      />
    ),
  })()
}

const styles = {
  modal: css`
    display: grid;
    grid-template-rows: 1fr auto;
    width: 62rem;
    max-width: 62rem;
    overflow: hidden;
    height: 80vh;
  `,
  sidebar: css`
    padding: 2rem 1.5rem 1.5rem 2rem;
  `,
}
